import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet'; // Add this import
import recentPuzzlesRaw from './new-puzzles-actual.json';
import WordLabel from './WordLabel';

import { AuthContext } from './GameWrapper';






import GameModals from './Modals';

let oldDailyGamesPromise = null;
let extendedGamesPromise = null;

const normalizeDailyGames = (games) => games.map((game, index) => ({
  ...game,
  id: game.id || (index + 1)
}));

const getBuildVersion = () => {
  if (process.env.NODE_ENV === 'development') {
    return 'dev';
  }

  if (typeof document === 'undefined') {
    return 'build';
  }

  const lastModified = Date.parse(document.lastModified);
  if (!Number.isNaN(lastModified)) {
    return String(lastModified);
  }

  const mainScript = Array.from(document.scripts).find((script) =>
    /\/static\/js\/main\.[^.]+\.js$/.test(script.src)
  );
  const match = mainScript?.src.match(/main\.([^.]+)\.js/);

  return match?.[1] || 'build';
};

const fetchPublicJson = async (fileName) => {
  const version = getBuildVersion();
  const response = await fetch(`${process.env.PUBLIC_URL}/${fileName}?v=${version}`);

  if (!response.ok) {
    throw new Error(`Failed to load ${fileName}`);
  }

  return response.json();
};

const recentDailyGames = normalizeDailyGames(recentPuzzlesRaw);

const loadOldDailyGames = async () => {
  if (!oldDailyGamesPromise) {
    oldDailyGamesPromise = fetchPublicJson('data.json').then((games) => normalizeDailyGames(games));
  }

  return oldDailyGamesPromise;
};

const loadExtendedGames = async () => {
  if (!extendedGamesPromise) {
    extendedGamesPromise = fetchPublicJson('data-extended.json');
  }

  return extendedGamesPromise;
};

const loadDailyGameById = async (requestedId, recentDailyGames) => {
  const recentGame = recentDailyGames.find(game => game.id === requestedId);

  if (recentGame) {
    return recentGame;
  }

  const oldDailyGames = await loadOldDailyGames();
  return oldDailyGames.find(game => game.id === requestedId) || null;
};

const loadDailyGameByPrintDate = async (printDate, recentDailyGames) => {
  const recentGame = recentDailyGames.find(game => game.print_date === printDate);

  if (recentGame) {
    return recentGame;
  }

  const oldDailyGames = await loadOldDailyGames();
  return oldDailyGames.find(game => game.print_date === printDate) || null;
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => {
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

const getTargetPrintDate = (archive) => {
  const targetDate = archive ? new Date(archive) : new Date();
  const today = new Date();

  targetDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (targetDate.getTime() > today.getTime()) {
    return formatLocalDate(today);
  }

  return formatLocalDate(targetDate);
};

function Game() {
    const [difficulty, setDifficulty] = useState('medium');
  const [currentGame, setCurrentGame] = useState(null);
  const [currentGuess, setCurrentGuess] = useState([]);
  const [foundGroups, setFoundGroups] = useState({});
  const [attempts, setAttempts] = useState(4);
  const [message, setMessage] = useState('');
  const [animation, setAnimation] = useState(null);
  const [animationWords, setAnimationWords] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [unlimitedGuesses, setUnlimitedGuesses] = useState(() => {
    return localStorage.getItem('connectionsUnlimitedGuesses') === 'true';
  });
  const [gameId, setGameId] = useState(null); // Add this line
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [selectionStreak, setSelectionStreak] = useState([]);
  const [isArchive, setIsArchive] = useState(false)
  const [gameDate, setGameDate] = useState(null)


  const levelColors = ['#fbd400', '#b5e352', '#729eeb', '#bc70c4'];
  const levelEmojis = ["🔵", "🟢", "🟡", "🔴", "🟣"]; // Choose your own emojis

  const { user, userProgress, completeGame, markGameAsPlayed } = useContext(AuthContext);






  // At the top of your Game.js, add the converter functions
const isValidOldFormat = (game) => {
  if (!game.groups || !game.startingGroups) {
    return false;
  }
  
  if (typeof game.groups !== 'object' || Array.isArray(game.groups)) {
    return false;
  }
  
  if (!Array.isArray(game.startingGroups)) {
    return false;
  }
  
  for (const groupName in game.groups) {
    const group = game.groups[groupName];
    if (!group.hasOwnProperty('level') || !Array.isArray(group.members)) {
      return false;
    }
    if (group.members.length !== 4) {
      return false;
    }
  }
  
  if (game.startingGroups.length !== 4) {
    return false;
  }
  
  for (const group of game.startingGroups) {
    if (!Array.isArray(group) || group.length !== 4) {
      return false;
    }
  }
  
  return true;
};

const convertNewFormatToOld = (gameData) => {
  // Check if the game is already in valid old format
  if (isValidOldFormat(gameData)) {
    return gameData;
  }

  if (!gameData.categories || !Array.isArray(gameData.categories)) {
    console.warn('Game data is neither valid old format nor valid new format:', gameData);
    return gameData;
  }

  const oldFormatGame = {
    id: gameData.id,
    groups: {},
    startingGroups: [],
    originalCards: [] // Add this to preserve card data
  };

  if (gameData.print_date) oldFormatGame.print_date = gameData.print_date;

  gameData.categories.forEach((category, index) => {
    const members = category.cards.map(card => 
      card.image_alt_text || card.content
    );
    
    oldFormatGame.groups[category.title] = {
      level: index,
      members: members
    };
    
    // Store original card data
    category.cards.forEach(card => {
      oldFormatGame.originalCards.push(card);
    });
  });

  const allWords = [];
  gameData.categories.forEach(category => {
    category.cards.forEach(card => {
      allWords.push({
        content: card.image_alt_text || card.content,
        position: card.position
      });
    });
  });

  allWords.sort((a, b) => a.position - b.position);
  const sortedWords = allWords.map(item => item.content);
  
  oldFormatGame.startingGroups = [
    sortedWords.slice(0, 4),
    sortedWords.slice(4, 8),
    sortedWords.slice(8, 12),
    sortedWords.slice(12, 16)
  ];

  return oldFormatGame;
};

const shouldUseNewFormatForDate = (archive) => {
  const CUTOFF_DATE = new Date("9/25/2025");
  CUTOFF_DATE.setHours(0, 0, 0, 0);
  
  let gameDate;
  
  if (archive) {
    gameDate = new Date(archive);
  } else {
    gameDate = new Date();
  }
  
  gameDate.setHours(0, 0, 0, 0);
  return gameDate >= CUTOFF_DATE;
};





  const fetchCustomGame = async (customId, slug) => {
    try {
      const param = customId ? `customId=${customId}` : `slug=${slug}`;
      const response = await fetch(`https://gg-apis.vercel.app/custom-connections/game?${param}`);
      const data = await response.json();
      if (response.ok) {
        return data;
      }
    } catch (error) {
      console.error('Error fetching custom game:', error);
    }
    return null;
  };

  useEffect(() => {
    setTimeElapsed(0);  // Reset the timer when starting a new game
  
    // Other codes
  
    const timer = setInterval(() => {
      setTimeElapsed(timeElapsed => timeElapsed + 1);
    }, 1000);
  
    return () => clearInterval(timer);
  }, [gameId]);  // Only depend on gameId




  useEffect(() => {
    if (!user && gameId) {  // Only track for non-logged in users with valid gameId
      const storedData = localStorage.getItem('gamesPlayedSession');
      let currentCount = 0;
      
      if (storedData) {
        const { count, timestamp } = JSON.parse(storedData);
        const ONE_DAY = 24 * 60 * 60 * 1000;
        
        // If data is less than 24 hours old, use it
        if (Date.now() - timestamp < ONE_DAY) {
          currentCount = count + 1;  // Increment the stored count
        }
      } else {
        currentCount = 1;  // First game
      }

      // Store updated count
      localStorage.setItem('gamesPlayedSession', JSON.stringify({
        count: currentCount,
        timestamp: Date.now()
      }));

      console.log(currentCount)

      // Only show login after second game
      if (currentCount === 2) {
        const loginBtn = document.getElementById('login-button');
        if (loginBtn) {
          loginBtn.click();
        }
      }
    }
  }, [gameId, user]);  // Depend on gameId and user

  useEffect(() => {
    localStorage.setItem('connectionsUnlimitedGuesses', unlimitedGuesses);
  }, [unlimitedGuesses]);

  useEffect(() => {
    setTimeElapsed(0);
    setShowAnswers(false);
    setIsArchive(false);
    setGameDate(null);
    setDifficulty('medium');
  
    const urlParams = new URLSearchParams(window.location.search);
    const requestedGameId = urlParams.get('gameId');
    const archive = urlParams.get('archive');
    const customId = urlParams.get('customId');
    const customSlug = urlParams.get('custom');
    setGameId(requestedGameId);

    let isMounted = true;
  
    const loadGame = async () => {
      if (customId || customSlug) {
        const customGame = await fetchCustomGame(customId, customSlug);
        if (customGame) {
          // Check if we should convert custom games based on the date being played
          if (shouldUseNewFormatForDate(archive) && !isValidOldFormat(customGame)) {
            if (isMounted) {
              setCurrentGame(convertNewFormatToOld(customGame));
            }
          } else {
            if (isMounted) {
              setCurrentGame(customGame);
            }
          }
          return;
        }
      }
    
      let gameToLoad;
    
      if(requestedGameId) {
        const numericGameId = parseInt(requestedGameId, 10);

        // Try to find game in main data
        if (numericGameId < 10000) {
          gameToLoad = await loadDailyGameById(numericGameId, recentDailyGames);
        } 
        // Try to find in extended data
        else {
          const extendedGames = await loadExtendedGames();
          const extendedIndex = numericGameId - 10000;
          gameToLoad = extendedGames[extendedIndex];
          if (gameToLoad) {
            gameToLoad = { ...gameToLoad, id: numericGameId };
          }

          setDifficulty(gameToLoad?.difficulty || 'medium');
        }
    
        // If game not found, load daily game
        if (!gameToLoad) {
          gameToLoad = await loadDailyGameByPrintDate(getTargetPrintDate(), recentDailyGames);
        }
      } else {
        const targetPrintDate = getTargetPrintDate(archive);

        if(archive) {
          setIsArchive(true);
          const [py, pm, pd] = targetPrintDate.split('-').map(Number);
          setGameDate(new Date(py, pm - 1, pd));
          gameToLoad = await loadDailyGameByPrintDate(targetPrintDate, recentDailyGames);
          setGameId(gameToLoad?.id || null);
        } else {
          setGameDate(new Date());
          gameToLoad = await loadDailyGameByPrintDate(targetPrintDate, recentDailyGames);
        }
      }

      if (!isMounted) {
        return;
      }
    
      // IMPORTANT: Check if we should convert the game based on the date being played
      if (gameToLoad && shouldUseNewFormatForDate(archive) && !isValidOldFormat(gameToLoad)) {
        gameToLoad = convertNewFormatToOld(gameToLoad);
      }
    
      if (isMounted) {
        setCurrentGame(gameToLoad);
      }
    };

    loadGame();
    setSelectionStreak([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      isMounted = false;
    };
  }, []);


  // Add this component inside Game function
const MarkAsPlayedButton = () => {
  const gameIdentifier = gameId || currentGame?.id;
  const [marked, setMarked] = useState(false);


  if (!gameId && !isArchive) {
    return null;
   }

   if( !user )
      return null;

  const handleMark = async () => {
    if (gameIdentifier && !marked) {
      await markGameAsPlayed(parseInt(gameIdentifier));
      setMarked(true);
      setMessage('Game marked as played');
    }
  };

  // Don't show button if game is already completed or marked
  if (!gameIdentifier || 
      userProgress?.completedGames?.includes(parseInt(gameIdentifier)) || 
      userProgress?.markedAsPlayed?.includes(parseInt(gameIdentifier))) {
    return null;
  }



  return (
    <button 
      className="game-btn" 
      onClick={handleMark}
      disabled={marked}
      style={{ width:'100%' }}
    >
      {marked ? 'Marked as Played' : 'Mark as Played'}
    </button>
  );
};

const changeDifficulty = async (newDifficulty) => {
  const extendedGames = await loadExtendedGames();

  const availableGamesWithDifficulty = extendedGames.filter(game => 
    game.difficulty === newDifficulty || 
    (newDifficulty === 'easy' && !game.difficulty)
  );
  
  // If we have games with this difficulty, select one randomly
  if (availableGamesWithDifficulty.length > 0) {
    const randomIndex = Math.floor(Math.random() * availableGamesWithDifficulty.length);
    const selectedGame = availableGamesWithDifficulty[randomIndex];
    const gameId = 10000 + extendedGames.indexOf(selectedGame);
    window.location.href = window.location.origin + '?gameId=' + gameId;
  } else {
    // If no games with this difficulty, pick a random game from dataExtended
    const randomIndex = Math.floor(Math.random() * extendedGames.length);
    const gameId = 10000 + randomIndex;
    window.location.href = window.location.origin + '?gameId=' + gameId;
  }
};

  const changeArchive = (newArchive) => {
    const formattedDate = `${newArchive.getMonth()+1}/${newArchive.getDate()}/${newArchive.getFullYear()}`;
    window.location.href = `${window.location.origin}?archive=${formattedDate}`;
  }

  const EARLIEST_GAME_DATE = new Date("6/12/2023");

  const goToPrevDate = () => {
    if (!gameDate) return;
    const prev = new Date(gameDate);
    prev.setDate(prev.getDate() - 1);
    changeArchive(prev);
  };

  const goToNextDate = () => {
    if (!gameDate) return;
    const next = new Date(gameDate);
    next.setDate(next.getDate() + 1);
    next.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Landing on today = latest game, go to the default daily view.
    if (next.getTime() >= today.getTime()) {
      window.location.href = window.location.origin;
    } else {
      changeArchive(next);
    }
  };

  // Update the URL to include new archive date
  

  function getDatesBetween(start, end) {
    let startDate = new Date(start);
    let endDate = new Date(end);
    let dates = [];
    while(startDate <= endDate) {
        dates.push(new Date(startDate));
        startDate.setDate(startDate.getDate() + 1);
    }
    return dates.reverse(); // reverse to get from today back to 6/12/2023
}

  function generateIndexConnections(archive) {
    const D = 864e5;
    const e = new Date("6/12/2023");
    let t = false;
    let today = new Date();
    today.setHours(0, 0, 0, 0);
  
    if( archive ) {
      let archiveDate = new Date(archive);
      archiveDate.setHours(0, 0, 0, 0);

      // If the archive date is after today's date, throw an error
      if( archiveDate.getTime() > today.getTime() ) {
        t = today - e.setHours(0, 0, 0, 0);
      }
      else {
        t = archiveDate - e.setHours(0, 0, 0, 0);
      }
    }
    else {
      t = today - e.setHours(0, 0, 0, 0);
    }
    
    let n = Math.round(t / D);
    return n < 0 ? Math.abs(n) : n;
  }

  const handleWordClick = word => {
    // If the word is already selected, remove it from the currentGuess
    if (currentGuess.includes(word)) {
      setCurrentGuess(currentGuess.filter(guess => guess !== word));
    } 
    // Else if there are less than 4 items in the currentGuess, add the word
    else if (currentGuess.length < 4) {
      setCurrentGuess([...currentGuess, word]);
    } 
    // If there are 4 items already and the word isn't one of them, ignore the click
    else {
      return;
    }
  };

  
  

  const submitGuess = () => {
    if (currentGuess.length !== 4) {
        setMessage('You need to select 4 items before submitting');
        return;
      }
      console.log(currentGame)
      const guessedGroup = Object.entries(currentGame.groups).find(
        ([groupName, group]) =>
          JSON.stringify(group.members.sort()) === JSON.stringify(currentGuess.sort())
      );

    if (guessedGroup) {
      setFoundGroups({
        ...foundGroups,
        [guessedGroup[0]]: guessedGroup[1].members,
      });

      const newStartingGroups = currentGame.startingGroups
        .flat()
        .filter(word => !currentGuess.includes(word));

      setCurrentGame({
        ...currentGame,
        startingGroups: chunkArray(newStartingGroups, 4),
      });
      
      setMessage('Congratulations! You have found a group!');
        setAnimation('success');
        setAnimationWords(currentGuess);
        setTimeout(() => {
            setCurrentGuess([]);
            setAnimation(null);
            setAnimationWords([]);
        }, 1000);
    } else {
        setAnimation('error');
        setAnimationWords(currentGuess);
        setTimeout(() => {
            setAnimation(null);
            setAnimationWords([]);
        }, 1000);
      if (unlimitedGuesses) {
        setMessage('Incorrect guess, please try again');
      } else {
        setMessage('Incorrect guess, please try again');
          setAttempts(prevAttempts => {
            if(prevAttempts - 1 === 0) {
                setGameOver(true);
                setMessage('Game Over! All attempts used up!');
                // Only count failure if it wasn't already completed
                if (!userProgress?.completedGames?.includes(Number(gameId))) {
                    completeGame(gameId, false);
                }
            }
            return prevAttempts - 1;
        });
      }
    }

    const wordLevels = currentGuess.map((word) => {
      // Find the group to which the word belongs
      const groupName = Object.keys(currentGame.groups).find(
        key => currentGame.groups[key].members.includes(word)
      );
    
      // Get the level of this group
      const level = currentGame.groups[groupName].level;
    
      // Return the word and its corresponding level
      return { word, level };
    });
  
    setSelectionStreak([...selectionStreak, wordLevels]);
  };

  const startNewGame = async () => {
    const [oldDailyGames, extendedGames] = await Promise.all([
      loadOldDailyGames(),
      loadExtendedGames()
    ]);

    const todayIndex = generateIndexConnections();
    const availableMainGames = oldDailyGames.filter(game => game.id < todayIndex);
    const extendedGamesWithIds = extendedGames.map((game, index) => ({
      ...game,
      id: 10000 + index
    }));
    
    const allAvailableGames = [...availableMainGames, ...extendedGamesWithIds];
    
    // Filter out both completed and marked games
    const playedGames = [
      ...(userProgress?.completedGames || []),
      ...(userProgress?.markedAsPlayed || [])
    ];
    const unplayedGames = allAvailableGames.filter(game => !playedGames.includes(game.id));
  
    if (unplayedGames.length === 0) {
      setMessage('You have played all available games!');
      return;
    }
    
    const randomIndex = Math.floor(Math.random() * unplayedGames.length);
    const newGameId = unplayedGames[randomIndex].id;
  
    window.location.href = window.location.origin + '?gameId=' + newGameId;
  };

  const restartGame = () => {
    window.location.reload();
  };

  const resetGuess = () => {
    setCurrentGuess([]);
  };

  const copyResultsToClipboard = () => {
    // Create a string from the selection streak
    const resultsString = selectionStreak.map((guess, index) => 
      guess.map((wordObj) => `${levelEmojis[wordObj.level]}`).join('')
    ).join('');
    window.focus();
    // Copy the string to the clipboard
    navigator.clipboard.writeText('ConnectionsUnlimited.org - ' + resultsString).then(function(){
      alert('Copied to Clipboard, you can now share the result in social media.');
    });
    // Show an alert message
  };

  
  const archiveDates = getDatesBetween("6/12/2023", new Date())
  const isSolved = currentGame && Object.keys(foundGroups).length === Object.keys(currentGame.groups).length;
  const remainingGroups = currentGame
    ? Object.entries(currentGame.groups)
        .filter(([groupName]) => !foundGroups[groupName])
        .sort(([, groupA], [, groupB]) => groupA.level - groupB.level)
    : [];

  const renderGroupMembers = (words) => {
    const groupCards = words.map(word =>
      currentGame?.originalCards?.find(card =>
        card.content === word || card.image_alt_text === word
      )
    );

    return (
      <div className="group-members">
        {groupCards.map((card, idx) => (
          card?.image_url ? (
            <img
              key={idx}
              src={card.image_url}
              alt={card.image_alt_text || words[idx]}
              className="group-member-image"
              style={{ maxHeight: '40px', margin: '0 5px' }}
            />
          ) : (
            <span key={idx}>{words[idx]}{idx < words.length - 1 ? ', ' : ''}</span>
          )
        ))}
      </div>
    );
  };

  useEffect(() => {
    const gameIdentifier = gameId || currentGame?.id;
    
    if (currentGame && 
        Object.keys(foundGroups).length === Object.keys(currentGame.groups).length && 
        gameIdentifier) {
        // Check if this game was already completed
        if (!userProgress?.completedGames?.includes(Number(gameIdentifier))) {
            completeGame(gameIdentifier, true);
            console.log('completed game');
        } else {
            console.log('game already completed before, not counting for streak');
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [foundGroups, currentGame, gameId]);

  const renderLoadingPlaceholder = () => (
    <div className="game-placeholder" aria-hidden="true">
      <div className="game-placeholder__top">
        <span className="game-placeholder__line game-placeholder__line--short"></span>
        <span className="game-placeholder__line game-placeholder__line--timer"></span>
      </div>
      <div className="game-board game-placeholder__board">
        {Array.from({ length: 16 }).map((_, index) => (
          <span className="game-item game-placeholder__tile" key={index}></span>
        ))}
      </div>
      <div className="game-placeholder__attempts"></div>
      <div className="btn-wrapper game-placeholder__buttons">
        <span className="game-btn game-placeholder__pill"></span>
        <span className="game-btn game-placeholder__pill"></span>
        <span className="game-btn game-placeholder__pill"></span>
      </div>
      <div className="difficulty-wrapper game-placeholder__difficulty">
        <span className="game-placeholder__chip"></span>
        <span className="game-placeholder__chip"></span>
        <span className="game-placeholder__chip"></span>
      </div>
      <div className="game-toggle-row game-placeholder__toggle">
        <span className="game-placeholder__switch"></span>
        <span className="game-placeholder__line game-placeholder__line--toggle"></span>
      </div>
    </div>
  );

  // dateModified for structured data: always "today" at 00:00 in US Eastern time.
  const SEO_TIME_ZONE = 'America/New_York';
  const seoDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: SEO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const seoOffsetPart = new Intl.DateTimeFormat('en-US', {
    timeZone: SEO_TIME_ZONE,
    timeZoneName: 'longOffset',
  })
    .formatToParts(new Date())
    .find((p) => p.type === 'timeZoneName');
  const seoOffset = seoOffsetPart
    ? seoOffsetPart.value.replace('GMT', '') || '+00:00'
    : '+00:00';
  const dateModified = `${seoDate}T00:00:00${seoOffset}`;

  return (
    <>
    <GameModals />
    { /*<div className="ad-bar">Try this <a href="https://www.nflgrid.org/" target="_blank">Play NFL Grid Wordle!</a> 🎮</div> */ }
    
    
    
      {gameId && (
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
      )}
        <Helmet>
        <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://www.connectionsunlimited.org/#organization",
            "name": "Connections Unlimited",
            "url": "https://www.connectionsunlimited.org/"
          },
          {
            "@type": "WebSite",
            "@id": "https://www.connectionsunlimited.org/#website",
            "url": "https://www.connectionsunlimited.org/",
            "name": "Connections Unlimited",
            "publisher": {
              "@id": "https://www.connectionsunlimited.org/#organization"
            }
          },
          {
            "@type": "WebPage",
            "@id": "https://www.connectionsunlimited.org/#webpage",
            "url": "https://www.connectionsunlimited.org/",
            "name": "Connections Unlimited",
            "isPartOf": {
              "@id": "https://www.connectionsunlimited.org/#website"
            },
            "dateModified": "${dateModified}"
          }
        ]
      }
    `}
  </script>
        </Helmet>
        {!currentGame ? (
          renderLoadingPlaceholder()
        ) : (
        <>
        {Object.entries(foundGroups).map(([groupName, words]) => {
            const groupLevel = currentGame?.groups?.[groupName]?.level ?? 0;

            return (
              <div
                className="game-group"
                key={groupName}
                style={{ backgroundColor: levelColors[groupLevel % levelColors.length] }}
              >
                <h3 className="group-name">{groupName}</h3>
                {renderGroupMembers(words)}
              </div>
            );
          })}

<div className="top-g">
  <div className="p-text">
    {gameId && !isArchive && (
      <>
        <strong>Connections Unlimited #{gameId}</strong>
      </>
    )}
    {((!gameId && !currentGame?.title) || isArchive) && (
      gameDate ? (
        (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const current = new Date(gameDate);
          current.setHours(0, 0, 0, 0);
          const earliest = new Date(EARLIEST_GAME_DATE);
          earliest.setHours(0, 0, 0, 0);
          const isToday = current.getTime() >= today.getTime();
          const hasPrev = current.getTime() > earliest.getTime();
          const puzzleNumber = Math.round((current.getTime() - earliest.getTime()) / 86400000) + 1;
          const arrowStyle = {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 4px',
            fontSize: '20px',
            lineHeight: 1,
            color: '#13141c',
            fontWeight: 700,
          };
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              {hasPrev && (
                <button type="button" style={arrowStyle} onClick={goToPrevDate} aria-label="Previous day">‹</button>
              )}
              <h2 style={{ margin: 0, fontSize: 'inherit', fontWeight: 700, display: 'inline' }}>Connections #{puzzleNumber} - {formatDisplayDate(gameDate)}</h2>
              {!isToday && (
                <button type="button" style={arrowStyle} onClick={goToNextDate} aria-label="Next day">›</button>
              )}
            </span>
          );
        })()
      ) : (
        <span>Create four groups of four.</span>
      )
    )}
    {/* Move isArchive check out of the badge condition */}
    {currentGame && userProgress?.completedGames?.includes(Number(gameId || currentGame?.id)) && (
      <span className="replay-badge" style={{ marginLeft: isArchive ? '0' : '10px' }}>
        🔄 Completed
      </span>
    )}
  </div>
  <div className="game-time"><span className="game-time-label">Time: </span>{`${Math.floor(timeElapsed / 60).toString().padStart(2, '0')}:${(timeElapsed % 60).toString().padStart(2, '0')}`}</div>
</div>

{currentGame && (currentGame.title || currentGame.author) && (
  <div className="custom-game-info">
    <h2 className="custom-game-title">{currentGame.title}</h2>
    <p className="custom-game-author">Created by {currentGame.author}</p>
  </div>
)}
      

      {currentGame && currentGame.startingGroups.map((group, groupIndex) => (
        <div className="game-board" key={groupIndex}>
          {group.map((word, wordIndex) => {
            // Find the original card data to check for images
            const cardData = currentGame.originalCards?.find(card => 
              card.content === word || card.image_alt_text === word
            );
            
            return (
              <button
                className={`game-item 
                  ${currentGuess.includes(word) ? 'selected' : ''} 
                  ${animationWords.includes(word) ? `${animation}-animation` : ''} 
                  ${cardData?.image_url ? 'has-image' : ''}`}
                key={wordIndex}
                onClick={() => handleWordClick(word)}
              >
                {cardData?.image_url ? (
                  <img 
                    src={cardData.image_url} 
                    alt={cardData.image_alt_text || word}
                    className="card-image"
                  />
                ) : (
                  <WordLabel word={word} />
                )}
              </button>
            );
          })}
        </div>
      ))}
      {message && <div className="message">{message}</div>}
      <div className="game-attempts">
        {unlimitedGuesses ? 'Mistakes remaining: Unlimited' : `Mistakes remaining: ${attempts}`}
      </div>
      {gameOver ? (
        <>
          <button className="game-btn" onClick={restartGame}>Restart the same Game</button>
          <button
            className="game-btn"
            onClick={startNewGame}
          >
            Start New Game
          </button>
          {remainingGroups.length > 0 && (
            <button
              className="game-btn"
              onClick={() => setShowAnswers(prevShowAnswers => !prevShowAnswers)}
            >
              {showAnswers ? 'Hide Answers' : 'Reveal Answers'}
            </button>
          )}
          {showAnswers && remainingGroups.length > 0 && (
            <div className="revealed-answers">
              <h2 className="revealed-answers__title">Answers</h2>
              <p className="revealed-answers__subtitle">
                Here are the remaining groups from this puzzle.
              </p>
              {remainingGroups.map(([groupName, group]) => (
                <div
                  className="game-group"
                  key={groupName}
                  style={{ backgroundColor: levelColors[group.level % levelColors.length] }}
                >
                  <h3 className="group-name">{groupName}</h3>
                  {renderGroupMembers(group.members)}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {isSolved ? (
            <>
                
                <div className="congratulations">
                    <h2>Congratulations!</h2>
                    <p>You have found all groups!</p>
                    <div className="share">
  {selectionStreak.map((guess, index) => (
    <div key={index} className="share-row">
      {guess.map((wordObj, i) => (
        <span key={i} style={{ backgroundColor: levelColors[wordObj.level] }}>
          
        </span>
      ))}
    </div>
  ))}
</div>
<button className="game-btn" style={{ backgroundColor:'#000000', color:'#fff', marginTop:'10px' }} onClick={copyResultsToClipboard}>Share Results</button>
                </div>
                <button
                  className="game-btn"
                  onClick={startNewGame}
                >
                  Start New Game
                </button>
            </>

          ) : (
            <>
            <div className="btn-wrapper">
              <button className="game-btn submit-btn" onClick={submitGuess}>Submit</button>
              <button className="game-btn" onClick={resetGuess}>Deselect</button>

              <button
                className="game-btn"
                onClick={startNewGame}
              >
                New Game
              </button>
                <MarkAsPlayedButton />
            </div>
            
            </>
          )}

          {!isArchive && !currentGame?.title && (
            <>
              <div className="difficulty-wrapper">
                <button 
                  onClick={() => changeDifficulty('easy')} 
                  className={difficulty === 'easy' ? 'selected' : ''}
                >
                  Easy
                </button>
                <button 
                  onClick={() => changeDifficulty('medium')} 
                  className={difficulty === 'medium' ? 'selected' : ''}
                >
                  Medium
                </button>
                <button 
                  onClick={() => changeDifficulty('hard')} 
                  className={difficulty === 'hard' ? 'selected' : ''}
                >
                  Hard
                </button>
              </div>
              <div className="game-toggle-row">
                <label className="game-switch">
                  <input
                    type="checkbox"
                    checked={unlimitedGuesses}
                    onChange={(e) => setUnlimitedGuesses(e.target.checked)}
                  />
                  <span className="game-slider"></span>
                </label>
                <span className="game-toggle-label">Unlimited guesses</span>
              </div>
            </>
          )}
          
          { !currentGame?.title && (
          <div className="archive-wrapper">
                <select 
                    onChange={e => changeArchive(new Date(e.target.value))} 
                >

                    <option key="" value="">--Select an archive date--</option>

                    {archiveDates.map(date => {
                        const formattedDate = `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`;
                        return (
                            <option key={formattedDate} value={formattedDate}>
                                {formattedDate}
                            </option>
                        )
                    })}
                </select>
          </div>
          )}
            
        </>
      )}
      </>
      )}
      
    
    </>
  );
}

function chunkArray(array, size) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

export default Game;
